/**
 * Машинный код ожидаемой ошибки доставки «заказ не выдан в СД» (бэкенд, tracking-history).
 * Подавление по коду надёжнее текста — формулировка сообщения на бэке менялась.
 */
const EXPECTED_DELIVERY_ERROR_CODE = 2;

/**
 * Проверяет, является ли ошибка ожидаемой для tracking-history доставки
 * (заказ ещё не заведён/не выдан в СД). Используется для подавления toast-уведомлений
 * по таким ожидаемым ошибкам.
 *
 * Подавление срабатывает на эндпоинте tracking-history при выполнении одного из условий:
 *   - совпал машинный `code` ожидаемой ошибки (надёжный признак); либо
 *   - текст ошибки совпал с паттерном «заказ не найден / не выдан» (подстраховка по тексту).
 *
 * @param detail - текст ошибки (detail или message из тела ответа)
 * @param status - HTTP статус ошибки (не используется в проверке)
 * @param endpoint - URL эндпоинта, который вернул ошибку
 * @param code - машинный код ошибки из тела ответа (если есть)
 * @returns true если это ожидаемая ошибка доставки — toast подавляется
 *
 * @example
 * ```typescript
 * isDeliveryNotFoundError('Указанный заказ не выдан в СД', 400,
 *   '/admin/delivery/12345/tracking-history', 2); // true (по коду)
 * ```
 */
export const isDeliveryNotFoundError = (
  detail: string | undefined,
  status: number | undefined,
  endpoint: string,
  code?: number
): boolean => {
  const isDeliveryTrackingEndpoint =
    endpoint.includes('/admin/delivery/') &&
    endpoint.includes('/tracking-history');

  if (!isDeliveryTrackingEndpoint) {
    return false;
  }

  // 1) Надёжный признак — машинный код ожидаемой ошибки.
  if (code === EXPECTED_DELIVERY_ERROR_CODE) {
    return true;
  }

  // 2) Подстраховка по тексту (на случай отсутствия кода или иной формулировки).
  if (typeof detail !== 'string') {
    return false;
  }

  const normalizedDetail = detail.replace(/\.*\s*$/, '').trim();

  const orderNotFoundPatterns = [
    /указанный\s+заказ\s+не\s+найден/i,
    /заказ\s+не\s+найден/i,
    /заказ\s+не\s+выдан/i,
    /order\s+not\s+found/i
  ];

  return orderNotFoundPatterns.some((pattern) =>
    pattern.test(normalizedDetail)
  );
};
